import { Head, useForm } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import type { FormEventHandler } from "react";

import InputError from "@/components/input-error";
import TextLink from "@/components/text-link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/layouts/auth-layout";
import {
  newSessionsPasswordlessPath,
  sessionsPasswordlessPath,
  signInPath,
} from "@/routes";

interface PasswordlessProps {
  sid?: string | null;
  redirect_to?: string | null;
  email?: string | null;
}

interface PasswordlessForm {
  email: string;
  sid: string;
  redirect_to: string;
}

export default function Passwordless({
  sid,
  redirect_to,
  email,
}: PasswordlessProps) {
  const isConfirm = Boolean(sid);
  const { data, setData, post, processing, errors, reset } =
    useForm<PasswordlessForm>({
      email: "",
      sid: sid ?? "",
      redirect_to: redirect_to ?? "",
    });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    post(sessionsPasswordlessPath(), {
      onFinish: () => reset("email"),
    });
  };

  return (
    <AuthLayout
      title={
        isConfirm ? "Confirmar acceso" : "Iniciar sesion sin contrasena"
      }
      description={
        isConfirm
          ? "Confirma para iniciar sesion con el enlace."
          : "Te enviaremos un link magico para ingresar."
      }
    >
      <Head title={isConfirm ? "Confirmar acceso" : "Iniciar sesion"} />

      <form onSubmit={submit}>
        <div className="grid gap-6">
          {isConfirm ? (
            <>
              {email && (
                <div className="grid gap-2">
                  <Label htmlFor="email">Correo electronico</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    className="mt-1 block w-full"
                    readOnly
                    disabled
                  />
                </div>
              )}

              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Si es tu primera vez, hace click en Continuar antes de iniciar sesion para no perder el enlace.
              </div>

              <Button type="submit" className="w-full" disabled={processing}>
                {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                Continuar
              </Button>
            </>
          ) : (
            <>
              <div className="grid gap-2">
                <Label htmlFor="email">Correo electronico</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                />
                <InputError message={errors.email} />
              </div>

              <Button type="submit" className="mt-4 w-full" disabled={processing}>
                {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                Enviar link
              </Button>
            </>
          )}
        </div>
      </form>

      <div className="text-muted-foreground space-x-1 text-center text-sm">
        <span>O tambien puedes</span>
        <TextLink href={signInPath()}>iniciar sesion</TextLink>
      </div>

      {isConfirm && (
        <div className="text-muted-foreground space-x-1 text-center text-sm">
          <span>Necesitas otro enlace?</span>
          <TextLink href={newSessionsPasswordlessPath()}>
            Solicitar nuevo link
          </TextLink>
        </div>
      )}
    </AuthLayout>
  );
}
